package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.EventDetailDto
import com.arena_web.data.dto.EventTeamDto
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

@Composable
fun MatchPerformance(
    event: EventDetailDto,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    // Single container matching Figma. Height is left to the caller so the
    // card can sit in either a Row (stretches via `align-items: stretch`)
    // or a Column where it should hug content.
    Column(
        modifier = modifier
            .sBorderRadius(36)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("padding", "${s(16)} ${s(18)} 0 ${s(18)}")
                property("border", "0.9px solid rgba(41,52,138,0.2)")
                property("background", "linear-gradient(30.4deg, rgba(0,59,174,0.03) 26.9%, rgba(3,87,230,0.03) 67.5%)")
            }
    ) {
        // Header: "Match Performance"
        SpanText(
            t.eventMatchPerformanceTitle,
            modifier = Modifier
                .sFontSize(24)
                .fontWeight(FontWeight.Medium)
                .sLineHeight(32)
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier { property("letter-spacing", s(0.24)) }
                .sPaddingLeft(8)
        )

        // Content area — Pro+ Stat empty state was removed, so the scoreboard
        // gets the full vertical budget. Margin-top trimmed (was 36 px) so
        // logos + names + score don't get clipped by the fixed 210 px host
        // container on the Match Info tab.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px)
                .sPadding(0, 18)
                .styleModifier { property("margin-top", s(12)) },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Scoreboard — supports 2+ teams
            if (event.teams.isNotEmpty()) {
                Scoreboard(teams = event.teams)
            }
        }
    }
}

@Composable
private fun Scoreboard(teams: List<EventTeamDto>) {
    Row(
        modifier = Modifier.fillMaxWidth().sPadding(0, 22),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top
    ) {
        teams.forEachIndexed { index, team ->
            // Team column: logo → name → score
            TeamScoreColumn(team = team)

            // Line divider between teams (not after last)
            if (index < teams.lastIndex) {
                // Vertical decorative line
                Box(
                    modifier = Modifier
                        .sWidth(60)
                        .sHeight(170)
                        .styleModifier {
                            property("align-self", "center")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Box(
                        modifier = Modifier
                            .sWidth(40)
                            .sHeight(3)
                            .styleModifier {
                                property("background", "rgba(190,214,245,0.6)")
                                property("border-radius", "2px")
                            }
                    )
                }
            }
        }
    }
}

@Composable
private fun TeamScoreColumn(team: EventTeamDto) {
    Column(
        modifier = Modifier.sGap(12),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Team logo or placeholder
        if (team.officialTeamImageUrl != null) {
            Image(
                src = team.officialTeamImageUrl!!,
                modifier = Modifier.sSize(88)
                    .styleModifier { property("object-fit", "contain") }
            )
        } else {
            val teamBg = team.color ?: "var(--l-s3, #03051F)"
            Box(
                modifier = Modifier
                    .sSize(88)
                    .sBorderRadius(100)
                    .overflow(Overflow.Hidden)
                    .position(Position.Relative)
                    .styleModifier {
                        property("border", "0.583px solid #272E32")
                        property("background", teamBg)
                    },
                contentAlignment = Alignment.Center
            ) {
                Image(
                    src = "/images/team-card-texture.png",
                    modifier = Modifier
                        .position(Position.Absolute)
                        .top(0.px).left(0.px)
                        .fillMaxSize()
                        .styleModifier {
                            property("opacity", "0.55")
                            property("object-fit", "cover")
                            property("pointer-events", "none")
                        }
                )
                Image(
                    src = "/images/team-card-glow.svg",
                    modifier = Modifier
                        .position(Position.Absolute)
                        .styleModifier {
                            property("width", s(140))
                            property("height", s(140))
                            property("left", s(-35))
                            property("top", s(-35))
                            property("pointer-events", "none")
                        }
                )
                Icon(
                    src = AppIcons.TeamPeople,
                    modifier = Modifier.sSize(43).zIndex(1)
                )
            }
        }

        // Team name
        SpanText(
            team.name.uppercase(),
            modifier = Modifier
                .sFontSize(24)
                .fontWeight(FontWeight.Medium)
                .sLineHeight(32)
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier {
                    property("letter-spacing", s(0.24))
                    property("white-space", "nowrap")
                }
        )

        // Score
        SpanText(
            "${team.score}",
            modifier = Modifier
                .sFontSize(46)
                .fontWeight(FontWeight.SemiBold)
                .sLineHeight(46)
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier { property("letter-spacing", s(0.46)) }
        )
    }
}

@Composable
private fun StatsEmptyState(modifier: Modifier = Modifier) {
    val t = LocalAppStrings.current
    Column(
        modifier = modifier.sGap(12),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        // Illustration + "Pro+ Stat" heading
        Column(
            modifier = Modifier.sGap(40),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Image(
                src = AppIcons.LogoStat,
                modifier = Modifier.sSize(256)
                    .styleModifier { property("object-fit", "contain") }
            )

            // "Pro+ Stat" text
            Row(
                modifier = Modifier.sGap(50).sHeight(48),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    t.eventProPlusLabel,
                    modifier = Modifier
                        .sFontSize(46)
                        .fontWeight(FontWeight.SemiBold)
                        .sLineHeight(46)
                        .styleModifier {
                            property("letter-spacing", s(0.46))
                            property("color", "#1E7FF5")
                        }
                )
                SpanText(
                    t.eventStatLabel,
                    modifier = Modifier
                        .sFontSize(46)
                        .fontWeight(FontWeight.SemiBold)
                        .sLineHeight(46)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier { property("letter-spacing", s(0.46)) }
                )
            }
        }

        // Description
        SpanText(
            t.eventProPlusStatDescription,
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(FontWeight.Medium)
                .sLineHeight(22)
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier {
                    property("letter-spacing", s(0.09))
                    property("text-align", "center")
                    property("max-width", s(400))
                }
        )
    }
}
